import React, { useMemo, useState, useCallback } from "react";
import { StatsByDay } from "../../../types/email-tracking.types";
import { format, parseISO } from "date-fns";

interface StatsByDayChartProps {
  data: StatsByDay[];
}

/** Series key shared between data shape, colours, and legend toggle. */
type SeriesKey = "sent" | "opens" | "clicks";

interface SeriesDef {
  key: SeriesKey;
  label: string;
  /** Bar fill (Tailwind class). */
  color: string;
  /** Bar fill on hover/focus of the day group. */
  colorHover: string;
}

/**
 * Email funnel series. Rendered as GROUPED bars (side by side), NOT stacked:
 * sent ⊇ opens, and clicks can exceed opens (one opener clicks several links),
 * so stacking them would sum to a meaningless total.
 */
const SERIES: SeriesDef[] = [
  {
    key: "sent",
    label: "Wysłane",
    color: "bg-neutral-400",
    colorHover: "bg-neutral-500",
  },
  {
    key: "opens",
    label: "Otwarcia",
    color: "bg-blue-400",
    colorHover: "bg-blue-500",
  },
  {
    key: "clicks",
    label: "Kliknięcia",
    color: "bg-emerald-500",
    colorHover: "bg-emerald-600",
  },
];

const formatDate = (dateStr: string): string => {
  try {
    return format(parseISO(dateStr), "MMM d");
  } catch {
    return dateStr;
  }
};

/**
 * Per-day email statistics chart: grouped bars for sent / opens / clicks with a
 * toggleable legend (click a series to show/hide it). Hand-rolled bars matching
 * the admin/outbox ThroughputChart style — the app has no charting library.
 */
export const StatsByDayChart: React.FC<StatsByDayChartProps> = ({ data }) => {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
  const [hiddenSeries, setHiddenSeries] = useState<Set<SeriesKey>>(new Set());

  const toggleSeries = useCallback((key: SeriesKey) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const visibleSeries = useMemo(
    () => SERIES.filter((s) => !hiddenSeries.has(s.key)),
    [hiddenSeries],
  );

  // Shared y-axis max across every visible bar (grouped bars are independent,
  // so the max is the largest single value, not a per-day sum).
  const maxCount = useMemo(() => {
    let max = 0;
    for (const day of data) {
      for (const s of visibleSeries) {
        if (day[s.key] > max) max = day[s.key];
      }
    }
    return max;
  }, [data, visibleSeries]);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <h3 className="text-sm font-semibold text-neutral-900 mb-4">
          Statystyki maili dziennie
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak danych dla wybranego okresu
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-4"
      data-testid="chart-stats-by-day"
      role="region"
      aria-label="Wykres statystyk maili dziennych"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-neutral-900">
          Statystyki maili dziennie
        </h3>

        {/* Series legend / toggles */}
        <div className="flex gap-2">
          {SERIES.map((s) => {
            const isHidden = hiddenSeries.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleSeries(s.key)}
                data-testid={`legend-toggle-${s.key}`}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors ${
                  isHidden
                    ? "opacity-40 line-through text-neutral-400"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`}
                aria-pressed={!isHidden}
                aria-label={`${isHidden ? "Pokaż" : "Ukryj"} serię ${s.label}`}
              >
                <span
                  className={`inline-block w-3 h-3 rounded-sm ${s.color}`}
                />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative flex gap-1 h-48">
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-xs text-neutral-500 pr-2 py-0.5">
          <span>{maxCount}</span>
          <span>{Math.round(maxCount / 2)}</span>
          <span>0</span>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative h-full">
          {visibleSeries.length === 0 ? (
            <div className="flex items-center justify-center h-full text-neutral-400 text-xs">
              Wszystkie serie ukryte — włącz przynajmniej jedną w legendzie
            </div>
          ) : (
            <div className="flex items-end gap-1 h-full">
              {data.map((day, index) => (
                <div
                  key={day.date}
                  className="flex-1 h-full flex flex-col justify-end items-center relative"
                  data-testid={`bar-group-${index}`}
                  onMouseEnter={() => setActiveTooltip(index)}
                  onMouseLeave={() => setActiveTooltip(null)}
                >
                  {/* Tooltip */}
                  <div
                    className={`absolute bottom-full mb-2 z-10 transition-opacity ${
                      activeTooltip === index
                        ? "opacity-100"
                        : "opacity-0 pointer-events-none"
                    }`}
                  >
                    <div className="bg-neutral-900 text-white text-xs rounded px-2 py-1.5 whitespace-nowrap">
                      <div className="font-medium mb-1">
                        {formatDate(day.date)}
                      </div>
                      {visibleSeries.map((s) => (
                        <div
                          key={s.key}
                          className="flex items-center gap-1.5"
                        >
                          <span
                            className={`inline-block w-2 h-2 rounded-sm ${s.color}`}
                          />
                          {s.label}: {day[s.key]}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Grouped bars for this day */}
                  <div
                    tabIndex={0}
                    role="img"
                    aria-label={`${formatDate(day.date)}: ${visibleSeries
                      .map((s) => `${s.label} ${day[s.key]}`)
                      .join(", ")}`}
                    className="w-full h-full flex items-end justify-center gap-0.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                    onFocus={() => setActiveTooltip(index)}
                    onBlur={() => setActiveTooltip(null)}
                    onTouchStart={() => setActiveTooltip(index)}
                  >
                    {visibleSeries.map((s) => {
                      const value = day[s.key];
                      const percentage =
                        maxCount > 0 ? (value / maxCount) * 100 : 0;
                      return (
                        <div
                          key={s.key}
                          data-testid={`bar-${s.key}-${index}`}
                          className={`flex-1 max-w-[14px] rounded-t transition-colors ${
                            activeTooltip === index ? s.colorHover : s.color
                          }`}
                          style={{
                            height: `${value > 0 ? Math.max(percentage, 2) : 0}%`,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-neutral-500 ml-12">
        {data.length > 0 && (
          <>
            <span>{formatDate(data[0].date)}</span>
            {data.length > 2 && (
              <span>{formatDate(data[Math.floor(data.length / 2)].date)}</span>
            )}
            {data.length > 1 && (
              <span>{formatDate(data[data.length - 1].date)}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StatsByDayChart;
